import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { productService } from "../../utils/services/productService";
import { generatedSalesCartService } from "../../utils/services/generated-sales-cart.service";
import { toast } from "react-toastify";

const createEmptyItem = () => ({
  product: null,
  sku: "",
  quantity: 1,
  price: 0,
});

const createInitialForm = () => ({
  customer: "",
  month: "",
  year: new Date().getFullYear(),
  status: "generated",
  items: [createEmptyItem()],
});

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "var(--bg-paper)",
    color: "var(--text-primary)",
    "& fieldset": { borderColor: "var(--border-color)" },
    "&:hover fieldset": { borderColor: "var(--text-secondary)" },
  },
  "& .MuiInputLabel-root": { color: "var(--text-secondary)" },
  "& .MuiFormHelperText-root": { marginLeft: 0 },
};

const GeneratedSalesCartModal = ({
  open,
  handleClose,
  onSubmitSuccess,
  selectedCart,
}) => {
  const [formData, setFormData] = useState(createInitialForm());
  const [formErrors, setFormErrors] = useState({});
  const isEditMode = Boolean(selectedCart?._id);

  useEffect(() => {
    if (!selectedCart) {
      setFormData(createInitialForm());
      setFormErrors({});
      return;
    }

    setFormData({
      customer: selectedCart.customer || "",
      month: selectedCart.month || "",
      year: selectedCart.year || new Date().getFullYear(),
      status: selectedCart.status || "generated",
      items: (selectedCart.items || []).length
        ? selectedCart.items.map((item) => ({
            product: item.product || null,
            sku: item.sku || "",
            quantity: item.quantity || 1,
            price: item.price || 0,
          }))
        : [createEmptyItem()],
    });
    setFormErrors({});
  }, [selectedCart, open]);

  const modalTitle = useMemo(
    () => `${isEditMode ? "Edit" : "Create"} Generated Sales Cart`,
    [isEditMode],
  );

  const resetAndClose = () => {
    setFormData(createInitialForm());
    setFormErrors({});
    handleClose?.();
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const updateItem = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    }));
    setFormErrors((prev) => ({ ...prev, [`item_${index}_${field}`]: "" }));
  };

  const addItemRow = () => {
    setFormData((prev) => ({ ...prev, items: [...prev.items, createEmptyItem()] }));
  };

  const removeItemRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      items:
        prev.items.length === 1
          ? [createEmptyItem()]
          : prev.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.customer?.trim()) {
      errors.customer = "Customer is required";
    }

    if (!formData.month || Number(formData.month) < 1 || Number(formData.month) > 12) {
      errors.month = "Month must be between 1 and 12";
    }

    if (!formData.year || Number(formData.year) < 2000) {
      errors.year = "Year is required";
    }

    if (!formData.status?.trim()) {
      errors.status = "Status is required";
    }

    if (!formData.items.length) {
      errors.items = "At least one item is required";
    }

    formData.items.forEach((item, index) => {
      if (!item.product?._id) {
        errors[`item_${index}_product`] = "Product is required";
      }
      if (!item.sku?.trim()) {
        errors[`item_${index}_sku`] = "SKU is required";
      }
      if (!item.quantity || Number(item.quantity) <= 0) {
        errors[`item_${index}_quantity`] = "Qty must be greater than 0";
      }
      if (item.price === "" || Number(item.price) < 0) {
        errors[`item_${index}_price`] = "Price must be 0 or more";
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => ({
    customer: formData.customer.trim(),
    month: Number(formData.month),
    year: Number(formData.year),
    status: formData.status.trim(),
    items: formData.items.map((item) => ({
      product: item.product._id,
      sku: item.sku.trim(),
      quantity: Number(item.quantity),
      price: Number(item.price),
    })),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      const payload = buildPayload();
      if (isEditMode) {
        await generatedSalesCartService.edit(selectedCart._id, payload);
        toast.success("Generated sales cart updated successfully.");
      } else {
        await generatedSalesCartService.create(payload);
        toast.success("Generated sales cart created successfully.");
      }
      onSubmitSuccess?.();
      resetAndClose();
    } catch (error) {
      console.error("Failed to save generated sales cart", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: "var(--bg-paper)",
          color: "var(--text-primary)",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          pt: 2,
        }}
      >
        <DialogTitle sx={{ p: 0, color: "var(--text-primary)" }}>
          {modalTitle}
        </DialogTitle>
        <IconButton onClick={resetAndClose} sx={{ color: "var(--text-secondary)" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: "var(--border-color)" }} />
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Customer"
              size="small"
              value={formData.customer}
              onChange={(event) => updateField("customer", event.target.value)}
              error={Boolean(formErrors.customer)}
              helperText={formErrors.customer}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Month"
              size="small"
              type="number"
              value={formData.month}
              onChange={(event) => updateField("month", event.target.value)}
              error={Boolean(formErrors.month)}
              helperText={formErrors.month}
              inputProps={{ min: 1, max: 12 }}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Year"
              size="small"
              type="number"
              value={formData.year}
              onChange={(event) => updateField("year", event.target.value)}
              error={Boolean(formErrors.year)}
              helperText={formErrors.year}
              inputProps={{ min: 2000 }}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Status"
              size="small"
              value={formData.status}
              onChange={(event) => updateField("status", event.target.value)}
              error={Boolean(formErrors.status)}
              helperText={formErrors.status}
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            border: "1px solid var(--border-color)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
              backgroundColor: "var(--table-header-bg)",
              borderBottom: "1px solid var(--border-color)",
            }}
          >
            <Typography fontWeight={600} color="var(--text-primary)">
              Cart Items
            </Typography>
            <Button
              onClick={addItemRow}
              size="small"
              startIcon={<AddIcon />}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: "var(--border-color)",
                color: "var(--text-primary)",
              }}
            >
              Add Item
            </Button>
          </Box>

          <Box sx={{ p: 2 }}>
            {formErrors.items && (
              <Typography sx={{ color: "error.main", mb: 1, fontSize: 12 }}>
                {formErrors.items}
              </Typography>
            )}

            {formData.items.map((item, index) => (
              <Box
                key={`cart-item-${index}`}
                sx={{
                  p: 2,
                  mb: index === formData.items.length - 1 ? 0 : 2,
                  border: "1px solid var(--border-color)",
                  borderRadius: 2,
                  backgroundColor:
                    index % 2 === 0 ? "var(--row-bg-even)" : "var(--row-bg-odd)",
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <AutocompleteWithPagination
                      selectedItem={item.product}
                      onDataChange={(product) => updateItem(index, "product", product)}
                      width="100%"
                      hideLabel
                      customLabel="Product"
                      optionKey="name"
                      backgroundColor="var(--bg-paper)"
                      apiEndPoint={(limit, skip, search, client) =>
                        productService.fetchNames(search, client, limit, skip)
                      }
                    />
                    {formErrors[`item_${index}_product`] && (
                      <Typography sx={{ color: "error.main", fontSize: 12, mt: 0.5 }}>
                        {formErrors[`item_${index}_product`]}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="SKU"
                      value={item.sku}
                      onChange={(event) => updateItem(index, "sku", event.target.value)}
                      error={Boolean(formErrors[`item_${index}_sku`])}
                      helperText={formErrors[`item_${index}_sku`]}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Quantity"
                      value={item.quantity}
                      onChange={(event) => updateItem(index, "quantity", event.target.value)}
                      error={Boolean(formErrors[`item_${index}_quantity`])}
                      helperText={formErrors[`item_${index}_quantity`]}
                      inputProps={{ min: 1 }}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Price"
                      value={item.price}
                      onChange={(event) => updateItem(index, "price", event.target.value)}
                      error={Boolean(formErrors[`item_${index}_price`])}
                      helperText={formErrors[`item_${index}_price`]}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton
                      onClick={() => removeItemRow(index)}
                      sx={{ color: "error.main" }}
                      aria-label="remove item"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          onClick={resetAndClose}
          sx={{
            textTransform: "none",
            borderColor: "var(--border-color)",
            color: "var(--text-primary)",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ textTransform: "none" }}
        >
          {isEditMode ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeneratedSalesCartModal;
